import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  MessageScenario,
  MessageTemplate,
  createMessageTemplate,
  deleteMessageTemplate,
  exportMessageTemplatesCsv,
  importMessageTemplatesCsv,
  listMessageScenarios,
  listMessageTemplates,
  updateMessageTemplate,
} from "@/services/messageTemplatesApi";
import { useToast } from "@/hooks/use-toast";
import { confirmDialog } from "@/components/ui/confirm-dialog";

/** Variables Jinja2 disponibles — miroir de VARIABLE_CHIPS cote front/. */
const VARIABLES = [
  "{{ candidate.firstname }}",
  "{{ candidate.lastname }}",
  "{{ candidate.title }}",
  "{{ candidate.company }}",
  "{{ recruiter.firstname }}",
  "{{ offer.title }}",
  "{{ offer.grade }}",
  "{{ offer.vision }}",
  "{{ booking_url }}",
];

/** Scenario "hors taxonomie" pour les cles qui ne matchent aucun prefixe. */
const ORPHAN_SCENARIO: MessageScenario = {
  prefix: "",
  label: "Hors scenario",
  description:
    "Cles ne correspondant a aucun scenario de contact connu — ces messages ne sont jamais selectionnes par l'automatisation.",
};

/** Rang dans la cadence, porte par le suffixe numerique de la cle. */
function keyIndex(key: string): number {
  const m = key.match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

export default function MessageTemplatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [scenarios, setScenarios] = useState<MessageScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newPosition, setNewPosition] = useState("0");
  const [newIsActive, setNewIsActive] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);

  // edit dialog
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [editKey, setEditKey] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editPosition, setEditPosition] = useState("0");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editError, setEditError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpls, scns] = await Promise.all([
        listMessageTemplates(navigate),
        listMessageScenarios(navigate),
      ]);
      setTemplates(tpls);
      setScenarios(scns);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Regroupement par scenario. Les cles orphelines sont rendues dans un groupe
   * dedie plutot que masquees : un message invisible ici mais present en base
   * serait seede dans les campagnes sans que personne ne puisse le corriger.
   */
  const groups = useMemo(() => {
    const known = scenarios.map((scenario) => ({
      scenario,
      items: templates
        .filter((t) => t.scenario === scenario.prefix)
        .sort((a, b) => keyIndex(a.key) - keyIndex(b.key)),
    }));
    const orphans = templates
      .filter((t) => !t.scenario)
      .sort((a, b) => a.key.localeCompare(b.key));
    return orphans.length
      ? [...known, { scenario: ORPHAN_SCENARIO, items: orphans }]
      : known;
  }, [templates, scenarios]);

  const resetCreateForm = (prefix = "") => {
    // Pre-remplit la cle avec le prochain rang libre du scenario, pour que
    // l'ordre de cadence reste continu sans calcul mental.
    if (prefix) {
      const used = templates
        .filter((t) => t.scenario === prefix)
        .map((t) => keyIndex(t.key))
        .filter((n) => n !== Number.MAX_SAFE_INTEGER);
      const next = used.length ? Math.max(...used) + 1 : 1;
      setNewKey(`${prefix}${next}`);
    } else {
      setNewKey("");
    }
    setNewContent("");
    setNewPosition(String(templates.length + 1));
    setNewIsActive(true);
    setCreateError(null);
  };

  const openCreate = (prefix = "") => {
    resetCreateForm(prefix);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreateError(null);
    if (!newKey.trim()) {
      setCreateError("La cle est requise.");
      return;
    }
    if (!newContent.trim()) {
      setCreateError("Le contenu est requis.");
      return;
    }
    const position = parseInt(newPosition, 10);
    if (Number.isNaN(position)) {
      setCreateError("La position doit etre un entier.");
      return;
    }
    try {
      await createMessageTemplate(
        {
          key: newKey.trim(),
          channel: "linkedin",
          language: "fr",
          content: newContent,
          position,
          is_active: newIsActive,
        },
        navigate,
      );
      setCreateOpen(false);
      void refresh();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Erreur de creation");
    }
  };

  const openEdit = (tpl: MessageTemplate) => {
    setEditing(tpl);
    setEditKey(tpl.key);
    setEditContent(tpl.content);
    setEditPosition(String(tpl.position));
    setEditIsActive(tpl.is_active);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setEditError(null);
    if (!editContent.trim()) {
      setEditError("Le contenu est requis.");
      return;
    }
    const position = parseInt(editPosition, 10);
    if (Number.isNaN(position)) {
      setEditError("La position doit etre un entier.");
      return;
    }
    try {
      await updateMessageTemplate(
        editing.id,
        {
          key: editKey.trim(),
          content: editContent,
          position,
          is_active: editIsActive,
        },
        navigate,
      );
      setEditing(null);
      void refresh();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Erreur de sauvegarde");
    }
  };

  const handleDelete = async (tpl: MessageTemplate) => {
    const ok = await confirmDialog({
      title: "Supprimer le message",
      description: `Le message "${tpl.key}" sera definitivement supprime de la bibliotheque standard. Les campagnes existantes conservent leur copie. Pour le retirer sans le perdre, desactivez-le plutot.`,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteMessageTemplate(tpl.id, navigate);
      void refresh();
    } catch (e) {
      toast({
        title: "Erreur de suppression",
        description: e instanceof Error ? e.message : "Erreur de suppression",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (tpl: MessageTemplate) => {
    try {
      await updateMessageTemplate(tpl.id, { is_active: !tpl.is_active }, navigate);
      void refresh();
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportMessageTemplatesCsv(navigate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "message-templates.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Erreur d'export",
        description: e instanceof Error ? e.message : "Erreur d'export",
        variant: "destructive",
      });
    }
  };

  const handleImportFile = async (file: File) => {
    const ok = await confirmDialog({
      title: "Importer la bibliotheque",
      description: `Le contenu de "${file.name}" REMPLACERA l'integralite de la bibliotheque standard (${templates.length} message(s)). Les campagnes existantes ne sont pas affectees.`,
      confirmLabel: "Importer",
      destructive: true,
    });
    if (!ok) return;
    setImporting(true);
    try {
      const rows = await importMessageTemplatesCsv(file, navigate);
      toast({
        title: "Import termine",
        description: `${rows.length} message(s) importe(s).`,
      });
      void refresh();
    } catch (e) {
      toast({
        title: "Erreur d'import",
        description: e instanceof Error ? e.message : "Erreur d'import",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const activeCount = templates.filter((t) => t.is_active).length;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Bibliotheque de messages</h1>
          </div>
        </header>

        <div className="space-y-4 p-6">
          <div className="flex items-start justify-between gap-4">
            <p className="max-w-3xl text-sm text-muted-foreground">
              Bibliotheque de messages pre-rediges standard, organisee par
              scenario de contact. C'est ce que herite toute nouvelle
              organisation qui n'a pas edite sa propre bibliotheque. Une
              modification prend effet a la creation de la{" "}
              <strong>prochaine</strong> campagne : les campagnes existantes
              conservent leur copie, et les candidats deja en cours de sequence
              portent des messages deja calcules.
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Exporter CSV
              </Button>
              <Button
                variant="outline"
                disabled={importing}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {importing ? "Import..." : "Importer CSV"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Reset avant l'await : sinon re-selectionner le MEME fichier
                  // apres une erreur ne redeclenche pas l'evenement change.
                  e.target.value = "";
                  if (file) void handleImportFile(file);
                }}
              />
              <Button onClick={() => openCreate()}>
                <Plus className="mr-2 h-4 w-4" />
                Nouveau message
              </Button>
            </div>
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          {loading ? (
            <div className="text-sm text-muted-foreground">Chargement...</div>
          ) : templates.length === 0 ? (
            <div className="rounded-md border p-6 text-sm text-muted-foreground">
              La bibliotheque standard est vide — les campagnes retombent sur les
              messages par defaut cables dans le code. Lancez{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                python scripts/seed_standard_message_templates.py
              </code>{" "}
              cote back/ pour la peupler, ou importez un CSV.
            </div>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">
                {templates.length} message(s), dont {activeCount} actif(s).
              </div>
              <div className="space-y-3">
                {groups.map(({ scenario, items }) => {
                  const isOpen = open[scenario.prefix] ?? false;
                  return (
                    <div
                      key={scenario.prefix || "orphans"}
                      className="rounded-lg border bg-card"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between p-4 text-left"
                        onClick={() =>
                          setOpen((prev) => ({
                            ...prev,
                            [scenario.prefix]: !isOpen,
                          }))
                        }
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0" />
                            )}
                            <h4 className="font-medium">{scenario.label}</h4>
                            <Badge variant="secondary" className="text-[10px]">
                              {items.length}
                            </Badge>
                            {scenario.prefix && (
                              <code className="text-xs text-muted-foreground">
                                {scenario.prefix}
                              </code>
                            )}
                          </div>
                          <p className="mt-1 pl-6 text-sm text-muted-foreground">
                            {scenario.description}
                          </p>
                        </div>
                      </button>

                      {isOpen && (
                        <div className="space-y-2 border-t p-4">
                          {items.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              Aucun message dans ce scenario.
                            </p>
                          ) : (
                            items.map((tpl, idx) => (
                              <div
                                key={tpl.id}
                                className="flex items-start justify-between gap-4 rounded-md border p-3"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">
                                      Message {idx + 1}
                                    </span>
                                    <code className="text-xs text-muted-foreground">
                                      {tpl.key}
                                    </code>
                                    {!tpl.is_active && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px]"
                                      >
                                        retire
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground">
                                    {tpl.content}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title={
                                      tpl.is_active
                                        ? "Retirer de la bibliotheque"
                                        : "Reactiver"
                                    }
                                    onClick={() => handleToggleActive(tpl)}
                                  >
                                    <EyeOff className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Editer"
                                    onClick={() => openEdit(tpl)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Supprimer"
                                    onClick={() => handleDelete(tpl)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                          {scenario.prefix && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openCreate(scenario.prefix)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Ajouter un message
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Creation */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Nouveau message</DialogTitle>
              <DialogDescription>
                La cle porte le scenario ET le rang dans la cadence, au format{" "}
                <code>scenario_N</code> (ex. <code>hunt_3</code>). Le contenu est
                un template Jinja2.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="mt-key">Cle</Label>
                  <Input
                    id="mt-key"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="ex: hunt_13"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mt-position">Position (ordre de seed)</Label>
                  <Input
                    id="mt-position"
                    value={newPosition}
                    onChange={(e) => setNewPosition(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="mt-content">Contenu</Label>
                <Textarea
                  id="mt-content"
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  rows={12}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Variables : {VARIABLES.join("  ")}
                </p>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Actif</Label>
                  <p className="text-xs text-muted-foreground">
                    Un message inactif n'est pas seede dans les nouvelles
                    campagnes.
                  </p>
                </div>
                <Switch checked={newIsActive} onCheckedChange={setNewIsActive} />
              </div>
              {createError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  {createError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newKey.trim() || !newContent.trim()}
              >
                Creer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edition */}
        <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Editer le message</DialogTitle>
              <DialogDescription>
                Prend effet a la creation de la prochaine campagne. Les campagnes
                existantes conservent leur copie.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="mt-edit-key">Cle</Label>
                  <Input
                    id="mt-edit-key"
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mt-edit-position">Position</Label>
                  <Input
                    id="mt-edit-position"
                    value={editPosition}
                    onChange={(e) => setEditPosition(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="mt-edit-content">Contenu</Label>
                <Textarea
                  id="mt-edit-content"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={14}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Variables : {VARIABLES.join("  ")}
                </p>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Actif</Label>
                  <p className="text-xs text-muted-foreground">
                    Un message inactif n'est pas seede dans les nouvelles
                    campagnes.
                  </p>
                </div>
                <Switch checked={editIsActive} onCheckedChange={setEditIsActive} />
              </div>
              {editError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  {editError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Annuler
              </Button>
              <Button onClick={handleSaveEdit} disabled={!editContent.trim()}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
